import {
    ArrowCwIcon,
    BoxIcon,
    CalendarIcon,
    CircleCrossIcon,
    CrownIcon,
    DepositIcon,
    EmailIcon,
    EquipmentIcon,
    ExportIcon,
    LockIcon,
    MixedChecklistIcon,
    MoneyBackIcon,
    PlusIcon,
    PrintIcon,
    SendIcon,
    ShapesIcon,
    SplitArrowIcon,
    StackIcon,
    UserIcon,
    WriteIcon,
} from "@xola/icons";
import {
    canCancel,
    canCancelItems,
    isPackage as checkPackage,
    getItemLineItemsByTypes,
    hasClaimedCancellationProtection,
    isEquipmentBased,
    isEquipmentItem,
    isEveryExperiencePurchaseItemCanceled,
    isGift,
    isGiftPurchaseItem,
    isMembershipItem,
    isMerchandiseItem,
    isMistPurchase,
    isOtherSellerPurchase,
    isPackageItem,
    isPackagePurchase,
    isPartnerPackagePurchase,
    isPurchaseItemCanceled,
    isPurchaseItemHasDate,
    isStoreCredit,
    PurchaseActionOption,
} from "common";
import { isUndefined } from "lodash";
import React from "react";
import {
    ExperiencePurchaseItem,
    FieldType,
    LineItemType,
    Permission,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseItemPrivacy,
} from "types";
import { ItemActionOption } from "../Common/ItemActionBar";

export const allItems = (
    purchase: Purchase,
    sellerId?: ID,
    item?: PurchaseItem,
    isTraveler = false,
): ItemActionOption[] => {
    // For MIST booking this is only show at root level (item is undefined)
    // For non-MIST bookings this is always shown (because item will always be defined)
    const shouldShowItemOrRootLevel = isMistPurchase(purchase) ? !isUndefined(item) : isUndefined(item);

    const isItemHasArrival =
        isMistPurchase(purchase) && isUndefined(item)
            ? purchase.items.some((item) => isPurchaseItemHasDate(item))
            : item && isPurchaseItemHasDate(item);

    const isMistPurchasePage = !item && isMistPurchase(purchase);
    const hasOnlyMerchandiseItems = purchase.items.every(isMerchandiseItem);
    const isExperiencePurchaseItemCanceled =
        (item && isPurchaseItemCanceled(item)) || isEveryExperiencePurchaseItemCanceled(purchase);

    const isSettledByCancellationProtection =
        isExperiencePurchaseItemCanceled &&
        (item
            ? hasClaimedCancellationProtection(item)
            : purchase.items.some((purchaseItem) => hasClaimedCancellationProtection(purchaseItem)));
    const isMerchandisePurchaseItem = isMerchandiseItem(item);

    const isNoGuestsContactData =
        !purchase.customerEmail &&
        !item?.guests?.find((guest) =>
            guest?.formResponse?.fields?.some((field) => field.type === FieldType.EMAIL && field.value),
        );
    const purchaseBelongsToPartner = sellerId ? isOtherSellerPurchase(purchase, sellerId) : false;
    const isPartnerPurchaseWithoutEditPermissions = purchaseBelongsToPartner && !purchase.partner?.editPurchases;
    const hasOnlyEquipmentItems = purchase.items.every(isEquipmentItem);

    return [
        {
            title: isTraveler ? "Change Arrival" : "Arrival",
            icon: <CalendarIcon />,
            action: PurchaseActionOption.CHANGE_ARRIVAL,
            permission: Permission.MODIFY_BOOKING_LITE,
            activeOnly: true,
            exclude:
                (item?.object &&
                    item.object !== PurchaseItemObject.EXPERIENCE &&
                    (item.object as string) !== PurchaseItemObject.EQUIPMENT) ||
                !isItemHasArrival ||
                isPartnerPurchaseWithoutEditPermissions,
        },
        {
            title: isTraveler
                ? "Change Guests"
                : item?.object === PurchaseItemObject.EQUIPMENT || hasOnlyEquipmentItems
                ? "Equipment"
                : "Guests",
            icon: item?.object === PurchaseItemObject.EQUIPMENT ? <EquipmentIcon /> : <UserIcon />,
            action: PurchaseActionOption.MODIFY_GUESTS,
            permission: Permission.MODIFY_BOOKING_LITE,
            activeOnly: true,
            exclude: isPartnerPurchaseWithoutEditPermissions,
        },
        {
            title: "Add-ons",
            icon: <BoxIcon />,
            action: PurchaseActionOption.EDIT_ADD_ONS,
            permission: Permission.MODIFY_BOOKING_LITE,
            exclude: isExperiencePurchaseItemCanceled || isPartnerPurchaseWithoutEditPermissions,
        },
        {
            title: "Update Fulfilled",
            icon: <MixedChecklistIcon />,
            action: PurchaseActionOption.UPDATE_FULFILLED_QUANTITY,
            permission: Permission.MODIFY_BOOKING_LITE,
            exclude:
                isPartnerPurchaseWithoutEditPermissions ||
                !(isMerchandisePurchaseItem || (isMistPurchasePage && hasOnlyMerchandiseItems)),
        },
        {
            title: "Change Quantity",
            icon: <ShapesIcon />,
            action: PurchaseActionOption.UPDATE_QUANTITY,
            permission: Permission.MODIFY_BOOKING_LITE,
            exclude:
                isPartnerPurchaseWithoutEditPermissions ||
                !(isMerchandisePurchaseItem || (isMistPurchasePage && hasOnlyMerchandiseItems)),
        },
        {
            title: "Refund",
            icon: <MoneyBackIcon />,
            action: PurchaseActionOption.REFUND,
            permission: Permission.MODIFY_BOOKING,
            // For MIST booking refund is only show at root level (item is undefined)
            // For non-MIST bookings refund is always shown (because item will always be defined)
            exclude:
                shouldShowItemOrRootLevel ||
                isPartnerPurchaseWithoutEditPermissions ||
                isSettledByCancellationProtection,
        },
        {
            title: isTraveler || isMerchandisePurchaseItem ? "Send Message" : "Message Guests",
            icon: <SendIcon />,
            action: PurchaseActionOption.MESSAGE,
            permission: Permission.MODIFY_BOOKING_LITE,
            activeOnly: true,
            exclude: purchaseBelongsToPartner,
        },
        {
            title: "Resend Confirmation",
            icon: <EmailIcon />,
            action: PurchaseActionOption.RESEND_CONFIRMATION,
            permission: Permission.MODIFY_BOOKING_LITE,
            exclude: isExperiencePurchaseItemCanceled,
        },
        {
            title: "Split Package",
            icon: <SplitArrowIcon />,
            action: PurchaseActionOption.SPLIT_PACKAGE,
            permission: Permission.MODIFY_BOOKING,
            activeOnly: true,
            exclude: !isPackagePurchase(purchase) || isPartnerPurchaseWithoutEditPermissions,
        },
        {
            title: "Resend Gratuity Notification",
            icon: <EmailIcon />,
            action: PurchaseActionOption.RESEND_GRATUITY_EMAIL,
            permission: Permission.RESEND_GRATUITY_EMAIL,
            exclude:
                isExperiencePurchaseItemCanceled ||
                isNoGuestsContactData ||
                !isItemHasArrival ||
                purchaseBelongsToPartner,
        },
        {
            title: "Resend Waiver Email",
            icon: <WriteIcon />,
            action: PurchaseActionOption.RESEND_WAIVER_EMAIL,
            permission: Permission.RESEND_WAIVER_EMAIL,
            exclude: purchaseBelongsToPartner,
        },
        {
            title: "Switch Experience",
            icon: <ArrowCwIcon />,
            action: PurchaseActionOption.SWITCH_EXPERIENCE,
            permission: Permission.MODIFY_BOOKING_LITE,
            activeOnly: true,
            exclude: purchaseBelongsToPartner || isEquipmentItem(item),
        },
        {
            title: "Print Receipt",
            icon: <PrintIcon />,
            permission: Permission.PRINT_RECEIPT,
            action: PurchaseActionOption.PRINT_RECEIPT,
        },
        {
            title: "Email Receipt",
            icon: <EmailIcon />,
            permission: Permission.MODIFY_BOOKING_LITE,
            action: PurchaseActionOption.EMAIL_RECEIPT,
            exclude: !purchase.customerEmail,
        },
        {
            title: item?.privacy === PurchaseItemPrivacy.PUBLIC ? "Make Private" : "Make Shared",
            icon: <LockIcon />,
            permission: Permission.MODIFY_BOOKING_LITE,
            activeOnly: true,
            action: PurchaseActionOption.EDIT_PURCHASE_PRIVACY,
            exclude:
                isPartnerPurchaseWithoutEditPermissions ||
                (isMistPurchasePage ? true : item && isPurchaseItemCanceled(item)),
        },
        {
            title: "Rebook Organizer",
            icon: <CrownIcon />,
            permission: Permission.MODIFY_BOOKING_LITE,
            action: PurchaseActionOption.REBOOK_ORGANIZER,
            activeOnly: true,
            exclude: shouldShowItemOrRootLevel || purchaseBelongsToPartner,
        },
        {
            title:
                item && getItemLineItemsByTypes(item, [LineItemType.GRATUITY])?.length > 0
                    ? "Modify Gratuity"
                    : "Add Gratuity",
            icon: <DepositIcon />,
            permission: Permission.RESEND_GRATUITY_EMAIL,
            action: PurchaseActionOption.MODIFY_GRATUITY,
            activeOnly: true,
            exclude: purchaseBelongsToPartner,
        },
        {
            title: "Export Tickets",
            icon: <ExportIcon />,
            action: PurchaseActionOption.EXPORT_TICKET,
            permission: Permission.EXPORT_TICKETS,
            exclude: isPartnerPurchaseWithoutEditPermissions,
        },
        {
            title: isMistPurchasePage ? "Cancel All" : isMerchandisePurchaseItem ? "Cancel" : "Cancel Reservation",
            icon: <CircleCrossIcon />,
            action: PurchaseActionOption.CANCEL_RESERVATION,
            permission: Permission.MODIFY_BOOKING_LITE,
            exclude:
                isPartnerPurchaseWithoutEditPermissions ||
                (isMistPurchasePage ? !canCancelItems(purchase) : item && !canCancel(item)),
            activeOnly: true,
        },
        {
            title: "Add Funds",
            icon: <PlusIcon />,
            action: PurchaseActionOption.ADD_FUNDS,
            activeOnly: true,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            title: "Adjust Balance",
            icon: <StackIcon />,
            action: PurchaseActionOption.ADJUST_BALANCE,
            activeOnly: true,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            // TODO: Duplicate of "Refund" above which uses action of PurchaseActionOption.REFUND
            title: "Refund",
            icon: <MoneyBackIcon />,
            action: PurchaseActionOption.REFUND_GIFT,
            permission: Permission.MODIFY_BOOKING,
            exclude: isPartnerPurchaseWithoutEditPermissions,
        },
        {
            title: "Send Certificate",
            icon: <SendIcon />,
            action: PurchaseActionOption.SEND_CERTIFICATE,
            activeOnly: true,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            title: "Send Gift Receipt Email",
            icon: <EmailIcon />,
            action: PurchaseActionOption.SEND_GIFT_RECEIPT_EMAIL,
            activeOnly: true,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            title: "Send Store Credit Email",
            icon: <EmailIcon />,
            action: PurchaseActionOption.SEND_STORE_CREDIT_EMAIL,
            activeOnly: true,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            title: "Print gift certificate",
            icon: <PrintIcon />,
            action: PurchaseActionOption.PRINT_GIFT_CERTIFICATE,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            title: "Send Gift Certificate to Recipient",
            icon: <EmailIcon />,
            action: PurchaseActionOption.SEND_GIFT_CERTIFICATE_TO_RECIPIENT,
            activeOnly: true,
            permission: Permission.MODIFY_BOOKING,
        },
        {
            title: "Resend Membership Email",
            icon: <EmailIcon />,
            action: PurchaseActionOption.SEND_MEMBERSHIP_EMAIL,
            activeOnly: true,
        },
    ];
};

const singleGiftActions = [
    PurchaseActionOption.ADD_FUNDS,
    PurchaseActionOption.ADJUST_BALANCE,
    PurchaseActionOption.REFUND_GIFT,
    PurchaseActionOption.PRINT_RECEIPT, // Print Receipt
    PurchaseActionOption.SEND_GIFT_RECEIPT_EMAIL, // Send receipt email
    PurchaseActionOption.SEND_CERTIFICATE, // Send receipt certificate
    PurchaseActionOption.PRINT_GIFT_CERTIFICATE, // Print gift certificate
    PurchaseActionOption.SEND_GIFT_CERTIFICATE_TO_RECIPIENT,
];

const mistGiftItemLevelActions = [
    PurchaseActionOption.ADD_FUNDS,
    PurchaseActionOption.ADJUST_BALANCE,
    PurchaseActionOption.REFUND_GIFT,
    PurchaseActionOption.SEND_CERTIFICATE,
    PurchaseActionOption.PRINT_GIFT_CERTIFICATE,
    PurchaseActionOption.SEND_GIFT_CERTIFICATE_TO_RECIPIENT,
];

const mistGiftOrderLevelActions = [PurchaseActionOption.REFUND_GIFT, PurchaseActionOption.PRINT_RECEIPT];

const singlePurchaseActions = [
    PurchaseActionOption.CHANGE_ARRIVAL,
    PurchaseActionOption.MODIFY_GUESTS,
    PurchaseActionOption.EDIT_ADD_ONS,
    PurchaseActionOption.REFUND,
    PurchaseActionOption.MESSAGE,
    PurchaseActionOption.RESEND_CONFIRMATION,
    PurchaseActionOption.RESEND_GRATUITY_EMAIL,
    PurchaseActionOption.RESEND_WAIVER_EMAIL,
    PurchaseActionOption.SWITCH_EXPERIENCE,
    PurchaseActionOption.PRINT_RECEIPT,
    PurchaseActionOption.EMAIL_RECEIPT,
    PurchaseActionOption.EDIT_PURCHASE_PRIVACY,
    PurchaseActionOption.REBOOK_ORGANIZER,
    PurchaseActionOption.MODIFY_GRATUITY,
    PurchaseActionOption.EXPORT_TICKET,
    PurchaseActionOption.CANCEL_RESERVATION,
];

const getMistActions = (purchase: Purchase) => {
    const hasGiftItem = purchase.items.some(isGiftPurchaseItem);
    const hasMembershipItem = purchase.items.some(isMembershipItem);
    const hasOnlyMerchandiseItems = purchase.items.every(isMerchandiseItem);
    const hasEquipmentItems = purchase.items.some(isEquipmentItem);
    const options: PurchaseActionOption[] = [];

    if (!hasGiftItem && !hasMembershipItem) {
        options.push(
            PurchaseActionOption.CHANGE_ARRIVAL,
            PurchaseActionOption.MODIFY_GUESTS,
            PurchaseActionOption.EDIT_ADD_ONS,
            PurchaseActionOption.EXPORT_TICKET,
        );
    }
    if (!hasGiftItem && !hasMembershipItem && !hasEquipmentItems) {
        options.push(PurchaseActionOption.SWITCH_EXPERIENCE);
    }

    if (hasOnlyMerchandiseItems) {
        options.push(PurchaseActionOption.UPDATE_FULFILLED_QUANTITY, PurchaseActionOption.UPDATE_QUANTITY);
    }

    options.push(
        PurchaseActionOption.REFUND,
        PurchaseActionOption.PRINT_RECEIPT,
        PurchaseActionOption.EMAIL_RECEIPT,
        PurchaseActionOption.REBOOK_ORGANIZER,
        PurchaseActionOption.CANCEL_RESERVATION,
    );

    return options;
};

const storeCreditActions = [
    PurchaseActionOption.ADD_FUNDS,
    PurchaseActionOption.REFUND,
    PurchaseActionOption.ADJUST_BALANCE,
    PurchaseActionOption.SEND_STORE_CREDIT_EMAIL,
];

const packageActions = [
    PurchaseActionOption.CHANGE_ARRIVAL,
    PurchaseActionOption.MODIFY_GUESTS,
    PurchaseActionOption.EDIT_ADD_ONS,
    PurchaseActionOption.REFUND,
    PurchaseActionOption.MESSAGE,
    PurchaseActionOption.RESEND_CONFIRMATION,
    PurchaseActionOption.RESEND_WAIVER_EMAIL,
    PurchaseActionOption.EMAIL_RECEIPT,
    PurchaseActionOption.SPLIT_PACKAGE,
    PurchaseActionOption.EDIT_PURCHASE_PRIVACY,
    PurchaseActionOption.REBOOK_ORGANIZER,
    PurchaseActionOption.EXPORT_TICKET,
    PurchaseActionOption.CANCEL_RESERVATION,
];

const packageActionsItemLevel = [
    PurchaseActionOption.CHANGE_ARRIVAL,
    PurchaseActionOption.MODIFY_GUESTS,
    PurchaseActionOption.EDIT_ADD_ONS,
    PurchaseActionOption.MODIFY_GRATUITY,
    PurchaseActionOption.MESSAGE,
    PurchaseActionOption.RESEND_CONFIRMATION,
    PurchaseActionOption.SPLIT_PACKAGE,
    PurchaseActionOption.RESEND_GRATUITY_EMAIL,
    PurchaseActionOption.RESEND_WAIVER_EMAIL,
    PurchaseActionOption.PRINT_RECEIPT,
    PurchaseActionOption.EMAIL_RECEIPT,
    PurchaseActionOption.EDIT_PURCHASE_PRIVACY,
    PurchaseActionOption.CANCEL_RESERVATION,
];

const singleMembershipActions = [
    PurchaseActionOption.REFUND,
    PurchaseActionOption.PRINT_RECEIPT,
    PurchaseActionOption.SEND_MEMBERSHIP_EMAIL,
];

const mistMembershipItemLevelActions = [PurchaseActionOption.PRINT_RECEIPT, PurchaseActionOption.SEND_MEMBERSHIP_EMAIL];

const mistMembershipOrderLevelActions = [PurchaseActionOption.PRINT_RECEIPT, PurchaseActionOption.REFUND];

const partnerPackageItemLevelActions = [
    PurchaseActionOption.CHANGE_ARRIVAL,
    PurchaseActionOption.MODIFY_GUESTS,
    PurchaseActionOption.REFUND,
    PurchaseActionOption.EDIT_ADD_ONS,
    PurchaseActionOption.MESSAGE,
    PurchaseActionOption.RESEND_CONFIRMATION,
    PurchaseActionOption.RESEND_GRATUITY_EMAIL,
    PurchaseActionOption.RESEND_WAIVER_EMAIL,
    PurchaseActionOption.PRINT_RECEIPT,
    PurchaseActionOption.EMAIL_RECEIPT,
    PurchaseActionOption.EDIT_PURCHASE_PRIVACY,
    PurchaseActionOption.CANCEL_RESERVATION,
];

const merchandiseItemLevelActions = [
    PurchaseActionOption.UPDATE_FULFILLED_QUANTITY,
    PurchaseActionOption.UPDATE_QUANTITY,
    PurchaseActionOption.MESSAGE,
    PurchaseActionOption.REFUND,
    PurchaseActionOption.PRINT_RECEIPT,
    PurchaseActionOption.EMAIL_RECEIPT,
    PurchaseActionOption.REBOOK_ORGANIZER,
    PurchaseActionOption.CANCEL_RESERVATION,
];

export enum ProductScreenType {
    SINGLE_GIFT = "single_gift",
    MIST_GIFT_ORDER_LEVEL = "mist_gift_order_level",
    MIST_GIFT_ITEM_LEVEL = "mist_gift_item_level",
    MIST = "mist",
    SINGLE_PURCHASE = "single_purchase",
    STORE_CREDIT = "store_credit",
    PACKAGE_LEVEL = "pkglevel",
    PACKAGE_ITEM_LEVEL = "pkgitem_level",
    SINGLE_MEMBERSHIP = "membership",
    MIST_MEMBERSHIP_ORDER_LEVEL = "mist_membership_order_level",
    MIST_MEMBERSHIP_ITEM_LEVEL = "mist_membership_item_level",
    PARTNER_PACKAGE_ITEM_LEVEL = "partner_pkg_item_level",
    MERCHANDISE_ITEM_LEVEL = "merchandise_item_level",
}

export const actionsByType = {
    [ProductScreenType.SINGLE_GIFT]: singleGiftActions,
    [ProductScreenType.MIST_GIFT_ORDER_LEVEL]: mistGiftOrderLevelActions,
    [ProductScreenType.MIST_GIFT_ITEM_LEVEL]: mistGiftItemLevelActions,
    [ProductScreenType.MIST]: getMistActions,
    [ProductScreenType.SINGLE_PURCHASE]: singlePurchaseActions,
    [ProductScreenType.STORE_CREDIT]: storeCreditActions,
    [ProductScreenType.PACKAGE_LEVEL]: packageActions,
    [ProductScreenType.PACKAGE_ITEM_LEVEL]: packageActionsItemLevel,
    [ProductScreenType.SINGLE_MEMBERSHIP]: singleMembershipActions,
    [ProductScreenType.MIST_MEMBERSHIP_ORDER_LEVEL]: mistMembershipOrderLevelActions,
    [ProductScreenType.MIST_MEMBERSHIP_ITEM_LEVEL]: mistMembershipItemLevelActions,
    [ProductScreenType.PARTNER_PACKAGE_ITEM_LEVEL]: partnerPackageItemLevelActions,
    [ProductScreenType.MERCHANDISE_ITEM_LEVEL]: merchandiseItemLevelActions,
};

export const getActionsByVariant = (variant: ProductScreenType, purchase: Purchase) => {
    const actions = actionsByType[variant];

    if (Array.isArray(actions)) {
        return actions;
    }

    return actions(purchase);
};

export const travelerPurchaseActionOption = [
    PurchaseActionOption.CHANGE_ARRIVAL,
    // PurchaseActionOption.MODIFY_GUESTS,
    // PurchaseActionOption.EDIT_ADD_ONS,
    PurchaseActionOption.MESSAGE,
    PurchaseActionOption.CANCEL_RESERVATION,
];

export const bookingEnginePurchaseActions = [
    PurchaseActionOption.PRINT_RECEIPT,
    PurchaseActionOption.EMAIL_RECEIPT,
    PurchaseActionOption.REBOOK_ORGANIZER,
];

export const getMenuVariant = (purchase: Purchase, selectedItem?: PurchaseItem): ProductScreenType => {
    const item = (selectedItem ?? purchase.items[0]) as ExperiencePurchaseItem;
    const isMistPage = !selectedItem && purchase.items.length > 1;

    const isPackage = checkPackage(item);
    const isCreditPurchase = isStoreCredit(purchase);

    const mistGift = isMistPurchase(purchase) && purchase.items.every((item) => isGift(item));
    const mistGiftOrderLevel = !selectedItem && mistGift;
    const mistGiftItemLevel = purchase.items.length > 1 && selectedItem && isGift(selectedItem);
    const singleGift = purchase.items.length === 1 && isGift(purchase.items[0]);

    const mistMembership = isMistPurchase(purchase) && purchase.items.every((item) => isMembershipItem(item));
    const mistMembershipOrderLevel = !selectedItem && mistMembership;
    const mistMembershipItemLevel = isMistPurchase(purchase) && selectedItem && isMembershipItem(selectedItem);
    const singleMembership = selectedItem && purchase.items.length === 1 && isMembershipItem(selectedItem);
    const partnerPackageItemLevel = isPartnerPackagePurchase(purchase) && isPackageItem(selectedItem);
    const merchandiseItemLevel = selectedItem && isMerchandiseItem(selectedItem);

    const menuVariant =
        (singleMembership && ProductScreenType.SINGLE_MEMBERSHIP) ||
        (mistMembershipOrderLevel && ProductScreenType.MIST_MEMBERSHIP_ORDER_LEVEL) ||
        (mistMembershipItemLevel && ProductScreenType.MIST_MEMBERSHIP_ITEM_LEVEL) ||
        (singleGift && ProductScreenType.SINGLE_GIFT) ||
        (mistGiftOrderLevel && ProductScreenType.MIST_GIFT_ORDER_LEVEL) ||
        (mistGiftItemLevel && ProductScreenType.MIST_GIFT_ITEM_LEVEL) ||
        (isCreditPurchase && ProductScreenType.STORE_CREDIT) ||
        (partnerPackageItemLevel && ProductScreenType.PARTNER_PACKAGE_ITEM_LEVEL) ||
        (isPackageItem(selectedItem) && ProductScreenType.PACKAGE_ITEM_LEVEL) ||
        (merchandiseItemLevel && ProductScreenType.MERCHANDISE_ITEM_LEVEL) ||
        (isPackage && ProductScreenType.PACKAGE_LEVEL) ||
        (isMistPage && ProductScreenType.MIST) ||
        ProductScreenType.SINGLE_PURCHASE;

    return menuVariant;
};
